import { apiErrorField, apiErrorMessage } from '@agos/api-client'
import {
  getGetCustomersQueryKey,
  useGetCustomers,
  useGetCustomersBulkAnonymizeJobId,
  useGetCustomersCustomerId,
  useGetLoyaltyLevels,
  useDeleteCustomersCustomerId,
  usePatchCustomersCustomerId,
  usePostCustomersBulkAnonymize,
  usePostCustomersBulkPoints,
  usePostCustomersBulkPush,
  usePostCustomersBulkSms,
  usePostCustomersSendEmail,
} from '@agos/api-client/customers'
import { OrderNumberLink, T, formatDay as sharedFormatDay, tt,
  orderStatusLabel,
  paymentLabel,
  useAuth,
  useModalFocus,
} from '@agos/shared'
import {
  BUTTON_DANGER,
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatCard,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  AlertTriangle,
  Bell,
  Mail,
  MessageSquare,
  Send,
  Star,
  UserCheck,
  UserX,
  Users,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import {
  DEFAULT_FILTERS,
  SEGMENT_FILTER_OPTIONS,
  activeFilterCount,
  anonymiseConfirmed,
  anonymisePhrase,
  audienceFor,
  bulkSendOutcome,
  audienceOfOne,
  averageOrderCents,
  clampedCount,
  directoryTotals,
  discountLabel,
  discountProblems,
  displayName,
  enrolCount,
  exclusionText,
  formatCents,
  formatCount,
  hasPersonalDiscount,
  ineligibleReason,
  isFinished,
  isNewsletterOnly,
  levelKeyOf,
  listQuery,
  parseStamp,
  pointsDelta,
  pointsProblems,
  progressPercent,
  reachableIds,
  recipientIds,
  renderForCustomer,
  singleRecipientLabel,
  smsCost,
  tierRank,
  unknownVariables,
  variableHelp,
  willBeTransliterated,
  type AnonymiseProgress,
  type Audience,
  type Channel,
  type Customer,
  type CustomerDetail,
  type Filters,
  type LoyaltyLevel,
  pointsNoticeNote,
} from './customer-directory'
import { ConsentRow } from './ConsentRow'
import { CustomerBlockSection } from './CustomerBlockSection'
import { customerActionsFor, emailRefusal } from './customer-rights'
import { PushConsentRow } from './PushConsentRow'

/**
 * `/marketing-customers` — the customer browser, and the only screen whose buttons reach real people.
 *
 * **FB-093.** `sendEmail` never consults `marketing_email_opt_in`, though its own comment says it
 * checks consent the way the SMS path does. 79 of the 2 059 addressable customers in the restored
 * data have said no. Legacy's interface has the hole in the same place — the per-customer button is
 * disabled on an opt-out, the bulk composer has no eligibility count at all. This page works out the
 * audience per channel, says who it left out and why, and sends only the rest.
 *
 * **FB-094.** The e-mail legend advertises `{{email}}` and `{{programName}}`; `render()` substitutes
 * `{{name}}`, `{{points}}` and `{{orders}}`, and only in the body. The legend now lists what the
 * server does, an unrecognised variable blocks the send, and the composer previews the message
 * against a real recipient.
 *
 * **FB-095.** `SmsGateway.stripDiacritics` runs on every message. The composer shows the text that
 * will actually be sent, counts segments on that, and multiplies by the recipients.
 *
 * **FB-096.** A newsletter-only row cannot be ticked; legacy guards the row click and not the box.
 *
 * Ported from legacy `pages/marketing-customers.tsx` (1 862 lines).
 */

interface Listing {
  customers?: Customer[]
  total?: number
  loyaltyTotal?: number
  newsletterOnlyCount?: number
}

export function MarketingCustomersPage() {
  const queryClient = useQueryClient()
  // §9.758, BACKEND.md §10A.413 - *Email* and *Anonymizovať* in the bar below ask their rights; they asked nothing.
  const { hasPermission } = useAuth()
  const may = customerActionsFor(hasPermission)

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [searchDraft, setSearchDraft] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set())
  const [openId, setOpenId] = useState<number | null>(null)
  const [composing, setComposing] = useState<Channel | null>(null)
  const [granting, setGranting] = useState(false)
  const [anonymising, setAnonymising] = useState(false)

  // Legacy debounces at 350 ms; the filter is what the server query is keyed on. A search that
  // narrows the list drops the selection with it, for the same reason `setFilter` does.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (current.search === searchDraft ? current : { ...current, search: searchDraft }))
      setSelected((current) => (current.size === 0 ? current : new Set()))
    }, 350)
    return () => clearTimeout(timer)
  }, [searchDraft])

  const { data, isLoading } = useGetCustomers(listQuery(filters))
  const listing = (data ?? {}) as Listing
  const customers = useMemo<Customer[]>(() => listing.customers ?? [], [listing.customers])
  const { data: levelData } = useGetLoyaltyLevels()
  const levels = useMemo<LoyaltyLevel[]>(
    () => ((levelData ?? []) as LoyaltyLevel[]).filter((level) => level.active !== false),
    [levelData],
  )

  const totals = directoryTotals(customers, listing.total ?? 0, listing.loyaltyTotal ?? 0)
  const filterCount = activeFilterCount(filters)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetCustomersQueryKey() })
  }

  /**
   * Changing a filter re-decides who is on the screen, so the selection goes with it.
   *
   * Cleared here rather than in an effect watching `filters`: a send must never carry somebody the
   * operator can no longer see, and an effect leaves one render in which it could.
   */
  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((current) => ({ ...current, [key]: value }))
    setSelected(new Set())
  }

  const toggle = (id: number) =>
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const columns: Column<Customer>[] = useMemo(
    () => [
      {
        key: 'select',
        header: '',
        render: (customer) => {
          const newsletter = isNewsletterOnly(customer)
          return (
            <input
              type="checkbox"
              checked={customer.id !== undefined && selected.has(customer.id)}
              // FB-096 — legacy guards the row click and leaves the box live, so a negative
              // synthetic id joins `recipientIds` and matches nobody.
              disabled={newsletter}
              onChange={() => customer.id !== undefined && toggle(customer.id)}
              // The row opens the drawer; ticking the box must not. Legacy stops it too.
              onClick={(event) => event.stopPropagation()}
              aria-label={
                newsletter
                  ? `${displayName(customer)} - ${tt('iba newsletter, nedá sa vybrať')}`
                  : `${tt('Vybrať')} ${displayName(customer)}`
              }
              title={newsletter ? tt('Iba newsletter - bez zákazníckeho záznamu') : tt('Vybrať')}
              className="w-4 h-4 rounded accent-primary"
            />
          )
        },
      },
      {
        key: 'name',
        header: tt('Meno / e-mail'),
        sortable: true,
        sortValue: (customer) => displayName(customer).toLowerCase(),
        render: (customer) => (
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate flex items-center gap-1.5">
              {customer.name || '-'}
              {isNewsletterOnly(customer) && (
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500 font-bold uppercase tracking-wider"
                  data-newsletter-badge
                >
                  Newsletter
                </span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground truncate">{customer.email || '-'}</p>
          </div>
        ),
      },
      {
        key: 'phone',
        header: tt('Telefón'),
        sortable: true,
        sortValue: (customer) => customer.phone ?? '',
        render: (customer) => (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {customer.phone || '-'}
          </span>
        ),
      },
      {
        key: 'orderCount',
        header: tt('Obj.'),
        sortable: true,
        sortValue: (customer) => customer.orderCount ?? 0,
        className: 'text-center',
        headerClassName: 'text-center',
        render: (customer) => (
          <span className="font-mono text-xs">{customer.orderCount ?? 0}</span>
        ),
      },
      {
        key: 'totalSpent',
        header: tt('Tržba'),
        sortable: true,
        sortValue: (customer) => averageOrderCents(customer) * (customer.orderCount ?? 0),
        className: 'text-right',
        headerClassName: 'text-right',
        render: (customer) => (
          <span className="font-mono text-xs whitespace-nowrap">
            {formatCents(averageOrderCents(customer) * (customer.orderCount ?? 0))}
          </span>
        ),
      },
      {
        key: 'level',
        header: 'Loyalty',
        sortable: true,
        sortValue: (customer) => tierRank(customer, levels),
        className: 'text-center',
        headerClassName: 'text-center',
        render: (customer) => <LevelBadge customer={customer} levels={levels} />,
      },
      {
        key: 'points',
        header: tt('Body'),
        sortable: true,
        sortValue: (customer) => customer.pointsBalance ?? 0,
        className: 'text-center',
        headerClassName: 'text-center',
        render: (customer) => (
          <span className="font-mono text-xs">{customer.pointsBalance ?? 0}</span>
        ),
      },
      {
        key: 'consent',
        header: tt('Súhlas'),
        sortable: true,
        sortValue: (customer) =>
          (customer.marketingEmailOptIn === false ? 0 : 2) +
          (customer.marketingSmsOptIn === false ? 0 : 1),
        className: 'text-center',
        headerClassName: 'text-center',
        render: (customer) => (
          <span className="inline-flex items-center gap-1.5 text-[11px]" data-consent>
            <ConsentMark channel="email" customer={customer} />
            <ConsentMark channel="sms" customer={customer} />
          </span>
        ),
      },
      {
        key: 'account',
        header: tt('Účet'),
        className: 'text-center',
        headerClassName: 'text-center',
        render: (customer) =>
          customer.deletionRequestedAt ? (
            <span
              className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-500"
              data-deactivated
            >
              <T>deakt.</T>
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              <T>aktívny</T>
            </span>
          ),
      },
    ],
    [selected, levels],
  )

  const emailAudience = audienceFor(customers, selected, 'email')
  const smsAudience = audienceFor(customers, selected, 'sms')
  const pushAudience = audienceFor(customers, selected, 'push')

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Zákazníci')}
        description={tt('Vyhľadávanie, filtre a hromadné akcie - e-mail, SMS, push, body.')}
        icon={<Users className="w-5 h-5" />}
      />

      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
        data-customer-totals
        data-customer-total={totals.total}
      >
        <StatCard
          label={tt('Zákazníkov')}
          value={formatCount(totals.total)}
          sub={totals.truncated ? `${tt('zobrazených')} ${formatCount(totals.loaded)}` : undefined}
          icon={Users}
          color="blue"
        />
        <StatCard label="Loyalty" value={formatCount(totals.loyaltyTotal)} icon={Star} color="amber" />
        <StatCard
          label={tt('Dá sa e-mailovať')}
          value={formatCount(totals.emailReachable)}
          sub={`${tt('odhlásených')} ${formatCount(totals.optedOutOfEmail)}`}
          icon={Mail}
          color="violet"
        />
        <StatCard
          label={tt('Dá sa SMS-kovať')}
          value={formatCount(totals.smsReachable)}
          icon={MessageSquare}
          color="emerald"
          testId="sms-reachable"
        />
      </div>

      {/* FB-093 - the population the server would have mailed regardless. */}
      {totals.optedOutOfEmail > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-consent-warning>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{totals.optedOutOfEmail}</strong>{' '}
            <T>
              zákazníkov má e-mail a marketing si vypnulo. Server ich pri hromadnom e-maile
              nevynecháva - táto obrazovka ich neposiela.
            </T>
          </span>
        </p>
      )}

      {totals.truncated && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-truncated>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>Server vrátil prvých</T> {totals.loaded} <T>z</T> {totals.total}.{' '}
            <T>Zúžte filtre - hromadná akcia sa týka len načítaných.</T>
          </span>
        </p>
      )}

      <FilterBar
        search={{
          value: searchDraft,
          onChange: setSearchDraft,
          placeholder: tt('Hľadať meno, e-mail alebo telefón...'),
        }}
        filters={
          <>
            <button
              type="button"
              onClick={() => setShowFilters((current) => !current)}
              className={cn(
                'px-3 py-2 rounded-xl border text-xs font-semibold transition-colors',
                filterCount > 0
                  ? 'bg-primary/10 border-primary/40 text-primary'
                  : 'border-border hover:bg-muted/50',
              )}
            >
              <T>Filtre</T>
              {filterCount > 0 && ` (${filterCount})`}
            </button>
            {filterCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setFilters(DEFAULT_FILTERS)
                  setSearchDraft('')
                  setSelected(new Set())
                }}
                className="px-3 py-2 rounded-xl border border-border text-xs font-semibold hover:bg-muted/50"
              >
                <T>Vynulovať</T>
              </button>
            )}
          </>
        }
      />

      {showFilters && (
        <div
          className="rounded-2xl border border-border bg-muted/30 p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          data-filter-panel
        >
          <FilterSelect
            showLabel
            value={filters.loyalty}
            label={tt('Vernostný program')}
            onChange={(value) => setFilter('loyalty', value as Filters['loyalty'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'yes', label: tt('Iba členovia') },
              { value: 'no', label: tt('Iba nečlenovia') },
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.orders}
            label={tt('Objednávky')}
            onChange={(value) => setFilter('orders', value as Filters['orders'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'yes', label: tt('Aspoň jedna') },
              { value: 'no', label: tt('Žiadna') },
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.emailOptIn}
            label={tt('E-mail marketing')}
            onChange={(value) => setFilter('emailOptIn', value as Filters['emailOptIn'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'in', label: tt('Súhlasia') },
              { value: 'out', label: tt('Odhlásení') },
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.smsOptIn}
            label={tt('SMS marketing')}
            onChange={(value) => setFilter('smsOptIn', value as Filters['smsOptIn'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'in', label: tt('Súhlasia') },
              { value: 'out', label: tt('Odhlásení') },
            ]}
          />
          {/*
            §9.283 - the third channel this page sends on. Worded for what push is: the subscription
            *is* the consent, granted in the customer's own browser, so there is nothing to be
            "odhlásený" from. Somebody with no device has usually never been asked.
          */}
          <FilterSelect
            showLabel
            value={filters.push}
            label={tt('Push marketing')}
            onChange={(value) => setFilter('push', value as Filters['push'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'has', label: tt('Má prihlásené zariadenie') },
              { value: 'none', label: tt('Bez zariadenia') },
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.deactivated}
            label={tt('Účet')}
            onChange={(value) => setFilter('deactivated', value as Filters['deactivated'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'no', label: tt('Iba aktívne') },
              { value: 'yes', label: tt('Iba deaktivované') },
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.level}
            label={tt('Úroveň')}
            onChange={(value) => setFilter('level', value)}
            options={[
              { value: 'any', label: tt('Všetky') },
              ...levels.map((level) => ({
                value: level.key ?? '',
                label: `${level.emoji ?? ''} ${level.name ?? ''}`.trim(),
              })),
            ]}
          />
          <FilterSelect
            showLabel
            value={filters.referral}
            label={tt('Odporúčania')}
            onChange={(value) => setFilter('referral', value as Filters['referral'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'referrer', label: tt('Niekoho odporučili') },
              { value: 'referred', label: tt('Boli odporúčaní') },
              { value: 'none', label: tt('Bez odporúčania') },
            ]}
          />
          {/* §9.207 - the categories Analytika names, so a campaign can be aimed at one. */}
          <FilterSelect
            showLabel
            value={filters.segment}
            label={tt('Segment')}
            onChange={(value) => setFilter('segment', value)}
            options={[...SEGMENT_FILTER_OPTIONS]}
          />
          <FilterSelect
            showLabel
            value={filters.newsletter}
            label="Newsletter"
            onChange={(value) => setFilter('newsletter', value as Filters['newsletter'])}
            options={[
              { value: 'any', label: tt('Všetci') },
              { value: 'subscribed', label: tt('Prihlásení') },
              { value: 'not_subscribed', label: tt('Neprihlásení') },
            ]}
          />
          <Field label={tt('Registrácia od')}>
            <input
              type="date"
              value={filters.registeredFrom}
              onChange={(event) => setFilter('registeredFrom', event.target.value)}
              aria-label={tt('Registrácia od')}
              className={INPUT}
            />
          </Field>
          <Field label={tt('Registrácia do')}>
            <input
              type="date"
              value={filters.registeredTo}
              onChange={(event) => setFilter('registeredTo', event.target.value)}
              aria-label={tt('Registrácia do')}
              className={INPUT}
            />
          </Field>
          <label className="flex items-end gap-2 text-xs pb-2">
            <input
              type="checkbox"
              checked={filters.includeNewsletterOnly}
              onChange={(event) => setFilter('includeNewsletterOnly', event.target.checked)}
              aria-label={tt('Zobraziť aj odberateľov newslettera')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Aj odberatelia newslettera</T>
          </label>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2" data-selection-bar>
        <button
          type="button"
          onClick={() => setSelected(new Set(reachableIds(customers, 'email')))}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          <T>Vybrať e-mailovateľných</T> ({reachableIds(customers, 'email').length})
        </button>
        <button
          type="button"
          onClick={() => setSelected(new Set(reachableIds(customers, 'sms')))}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          <T>Vybrať SMS-ovateľných</T> ({reachableIds(customers, 'sms').length})
        </button>
        {/* §9.260 - the owner's O5. The third channel, which the picker could not offer because the
          * list carried no device count; §10A.92 put one on it. About four per cent of the directory
          * — 104 of 2 463 — which is exactly why guessing *everybody* would have been so wrong. */}
        <button
          type="button"
          data-select-push
          onClick={() => setSelected(new Set(reachableIds(customers, 'push')))}
          className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
        >
          <T>Vybrať push-ovateľných</T> ({reachableIds(customers, 'push').length})
        </button>
        {selected.size > 0 && (
          <>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50"
            >
              <T>Zrušiť výber</T>
            </button>
            <div className="flex flex-wrap items-center gap-2 rounded-xl border-2 border-primary/40 bg-primary/5 px-3 py-1.5">
              <span className="text-xs font-bold text-primary">
                {selected.size} <T>vybraných</T>
              </span>
              {may.email && (
                <BulkButton icon={Mail} label="Email" count={emailAudience.eligible.length} onClick={() => setComposing('email')} />
              )}
              <BulkButton icon={MessageSquare} label="SMS" count={smsAudience.eligible.length} onClick={() => setComposing('sms')} />
              {/* §10A.92 - the only one of the five without a count, because there was nothing to
                * count with. Its two neighbours have always had one. */}
              <BulkButton icon={Bell} label="Push" count={pushAudience.eligible.length} onClick={() => setComposing('push')} />
              <BulkButton icon={Star} label={tt('Body')} onClick={() => setGranting(true)} />
              {may.anonymize && (
                <BulkButton icon={UserX} label={tt('Anonymizovať')} tone="red" onClick={() => setAnonymising(true)} />
              )}
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <div data-customers>
          <DataTable<Customer>
            columns={columns}
            data={customers}
            rowKey={(customer) => customer.id ?? 0}
            onRowClick={(customer) => {
              if (!isNewsletterOnly(customer) && customer.id !== undefined) setOpenId(customer.id)
            }}
            pageSize={50}
            compact
            emptyMessage={tt('Žiadni zákazníci nezodpovedajú filtru.')}
            emptyIcon={<Users className="w-10 h-10 mb-3 opacity-30" />}
          />
        </div>
      )}

      {openId !== null && (
        <CustomerDrawer
          customerId={openId}
          levels={levels}
          onClose={() => setOpenId(null)}
          onChanged={invalidate}
        />
      )}

      {composing !== null && (
        <ComposeDialog
          audience={
            composing === 'email' ? emailAudience : composing === 'sms' ? smsAudience : pushAudience
          }
          onClose={() => setComposing(null)}
          onSent={() => {
            setComposing(null)
            setSelected(new Set())
            invalidate()
          }}
        />
      )}

      {granting && (
        <PointsDialog
          customers={customers}
          selected={selected}
          onClose={() => setGranting(false)}
          onDone={() => {
            setGranting(false)
            setSelected(new Set())
            invalidate()
          }}
        />
      )}

      {anonymising && (
        <AnonymiseDialog
          ids={[...selected]}
          onClose={() => setAnonymising(false)}
          onDone={() => {
            setAnonymising(false)
            setSelected(new Set())
            invalidate()
          }}
        />
      )}

      {/* The one thing worth repeating wherever a send button lives. */}
      <p className="text-[11px] text-muted-foreground">
        <T>
          E-mail, SMS aj push idú skutočným ľuďom. Každá hromadná akcia najprv povie, koľkých sa
          dotkne a koho vynecháva.
        </T>
      </p>
    </div>
  )
}

// ─── the composer ────────────────────────────────────────────────────────────

const CHANNEL_TITLE: Record<Channel, string> = {
  email: tt('Hromadný e-mail'),
  sms: tt('Hromadná SMS'),
  push: tt('Push notifikácia'),
}

/**
 * The same dialog, opened on one person - §9.336.
 *
 * *Hromadný e-mail - 1 príjemcov* is what the bulk title produces for a single recipient: the wrong
 * word (**hromadný** means bulk) and the wrong case (**príjemcov** is the plural). The drawer opens
 * this dialog for exactly one customer every time, so the one-recipient wording is the common case
 * now rather than an edge of the selection.
 */
const ONE_RECIPIENT_TITLE: Record<Channel, string> = {
  email: tt('E-mail'),
  sms: tt('SMS'),
  push: tt('Push notifikácia'),
}

function ComposeDialog({
  audience,
  onClose,
  onSent,
}: {
  audience: Audience
  onClose: () => void
  onSent: () => void
}) {
  const { toast } = useToast()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [url, setUrl] = useState('')
  const [confirming, setConfirming] = useState(false)

  // Declared before the variable checks below, which ask it whether there is a subject to check at all.
  const needsSubject = audience.channel === 'email'
  const recipients = audience.eligible.length
  const preview = audience.eligible[0]
  /* FB-094 - a variable name nobody implemented arrives as literal braces in every one of thousands of
     messages, so it is named before the send rather than discovered afterwards.

     **§9.484 - the subject is checked the same way, and the old blanket refusal is gone.** This read
     `subjectHasVariable`, blocking **any** `{{…}}` in a subject on the premise that *a subject line is never
     substituted* - true when written. `CustomerBulkService.sendEmail` renders **both** now, and its own
     comment records the fix: *"Rendered too. A variable in a subject line went out literally to everybody."*
     So refusing `Ahoj {{name}}` was the client refusing what the server accepts, which §3.25 calls a rule
     nobody can find. The question for a subject is the same as for a body: is it one of the three? */
  const unknownInBody = unknownVariables(body, audience.channel)
  const unknownInSubject = needsSubject ? unknownVariables(subject, audience.channel) : []
  const cost = smsCost(body, recipients)

  const filled = body.trim() !== '' && (!needsSubject || subject.trim() !== '')
  const canSend =
    filled && recipients > 0 && unknownInBody.length === 0 && unknownInSubject.length === 0
  // §9.336 - empty for a selection, so the count stays the question there.
  const onlyRecipient = singleRecipientLabel(audience)

  const onError = (error: unknown) =>
    toast({
      title: tt('Nepodarilo sa odoslať'),
      // §9.364 - `messageOf` read `record.error ?? record.message` off an `ApiError`, which has no
      // `error` property and whose `message` is the **developer** string, so a missing BulkGate
      // configuration reached the operator as "HTTP 400 Bad Request: BulkGate nie je
      // nakonfigurovaný". `apiErrorMessage` is the reader that exists so §6's rule holds: the
      // server's sentence as-is, with no status glued to the front.
      description: apiErrorMessage(error, tt('Nič sa neodoslalo.')),
      variant: 'destructive',
    })
  /**
   * §9.364, FB-651 - what the server actually reported.
   *
   * This read `sent` and `failed` and dropped `error`, `errors[]`, `skipped` and
   * `eligibleTargets` - so *"Odoslané: 0"* was the whole of what an operator got, twice, for a push
   * whose reason the server was carrying. And `sent ?? recipients` reported every recipient as sent
   * when the field was missing.
   */
  const report = (result: unknown) => {
    const outcome = bulkSendOutcome(result, recipients)
    toast({
      title: outcome.title,
      description: outcome.description,
      ...(outcome.ok ? {} : { variant: 'destructive' as const }),
    })
    onSent()
  }

  const sendEmail = usePostCustomersSendEmail({ mutation: { onSuccess: report, onError } })
  const sendSms = usePostCustomersBulkSms({ mutation: { onSuccess: report, onError } })
  const sendPush = usePostCustomersBulkPush({ mutation: { onSuccess: report, onError } })
  const pending = sendEmail.isPending || sendSms.isPending || sendPush.isPending

  const send = () => {
    const ids = recipientIds(audience)
    if (audience.channel === 'email') {
      // FB-093 — `recipientIds` here is the *filtered* list. Handing the server the raw selection
      // would mail whoever opted out, because its query does not look.
      sendEmail.mutate({ data: { recipientIds: ids, subject: subject.trim(), body } })
      return
    }
    if (audience.channel === 'sms') {
      sendSms.mutate({ data: { customerIds: ids, message: body } })
      return
    }
    sendPush.mutate({
      data: {
        customerIds: ids,
        title: subject.trim() || body.slice(0, 60),
        messageBody: body,
        ...(url.trim() === '' ? {} : { url: url.trim() }),
      },
    })
  }

  return (
    <Modal
      title={
        onlyRecipient === ''
          ? `${CHANNEL_TITLE[audience.channel]} - ${recipients} ${tt('príjemcov')}`
          : `${ONE_RECIPIENT_TITLE[audience.channel]} - ${onlyRecipient}`
      }
      onClose={onClose}
      wide
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={!canSend || pending}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm flex items-center justify-center gap-2')}
          >
            {pending ? <Spinner className="w-4 h-4" /> : <Send className="w-4 h-4" />}
            <T>Odoslať</T>
          </button>
        </>
      }
    >
      {/* FB-093 - who is left out, and why, before anything is typed. */}
      <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1" data-audience>
        <p className="text-xs font-bold">
          {recipients} <T>z</T> {audience.selected} <T>vybraných dostane túto správu.</T>
        </p>
        {Object.entries(audience.reasons).length > 0 && (
          <p className="text-xs text-muted-foreground" data-audience-excluded>
            <T>Vynechaní:</T>{' '}
            {Object.entries(audience.reasons)
              .map(([reason, count]) => exclusionText(reason as never, count))
              .join(' · ')}
          </p>
        )}
        {audience.channel === 'email' && (audience.reasons['opted-out'] ?? 0) > 0 && (
          <p className="text-xs text-amber-500" data-audience-consent>
            <T>
              Server pri hromadnom e-maile súhlas nekontroluje - bez tejto obrazovky by im e-mail
              odišiel.
            </T>
          </p>
        )}
        {/* §10A.92 - this said *"koľkých to je, rozhodne server"*, because the list carried no device
          * count. It carries one now, so the count above is the answer and this sentence would be a
          * apology for a number that is right. `serverDecides` is false for every channel and the
          * block is kept as the place to put one back if a channel ever needs it. */}
        {audience.serverDecides && (
          <p className="text-xs text-muted-foreground" data-audience-server>
            <T>Koľkých to je, rozhodne server.</T>
          </p>
        )}
      </div>

      {recipients === 0 ? (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-audience-empty>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <T>Nikto z vybraných túto správu dostať nemôže.</T>
        </p>
      ) : null}

      {/* FB-094 - what the server substitutes, and nothing else. */}
      <div className="rounded-xl border border-border p-3 space-y-1" data-variables>
        <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
          <T>Premenné, ktoré server nahradí</T>
        </p>
        {variableHelp(audience.channel).map((help) => (
          <p key={help.token} className="text-xs">
            <code className="px-1 rounded bg-muted font-mono">{help.token}</code>{' '}
            <span className="text-muted-foreground">{help.description}</span>
          </p>
        ))}
      </div>

      {needsSubject && (
        <Field label={tt('Predmet')}>
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            aria-label={tt('Predmet')}
            className={INPUT}
          />
        </Field>
      )}
      {audience.channel === 'push' && (
        <Field label={tt('Titulok')}>
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            aria-label={tt('Titulok')}
            className={INPUT}
          />
        </Field>
      )}

      <Field label={audience.channel === 'sms' ? tt('Text SMS') : tt('Text správy')}>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={audience.channel === 'sms' ? 4 : 8}
          aria-label={tt('Text správy')}
          className={cn(INPUT, 'resize-none font-mono')}
        />
      </Field>

      {audience.channel === 'push' && (
        <Field label={tt('Odkaz (voliteľné)')}>
          <input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            aria-label={tt('Odkaz')}
            className={INPUT}
          />
        </Field>
      )}

      {/* §9.484 - **which field**, because a composer has two and *`{{email}}` is not substituted* sends the
          operator looking in the wrong box. One line per field that has one. */}
      {unknownInSubject.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-unknown-variables="subject">
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>V predmete server nepozná</T>{' '}
            {unknownInSubject.map((name) => `{{${name}}}`).join(', ')} -{' '}
            <T>dorazí to ako text v každej jednej správe.</T>
          </span>
        </p>
      )}
      {unknownInBody.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-red-500" data-unknown-variables="body">
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <T>V texte server nepozná</T>{' '}
            {unknownInBody.map((name) => `{{${name}}}`).join(', ')} -{' '}
            <T>dorazí to ako text v každej jednej správe.</T>
          </span>
        </p>
      )}

      {/* FB-095 - what an SMS will actually say, and what it will cost. */}
      {audience.channel === 'sms' && body !== '' && (
        <div className="rounded-xl border border-border bg-muted/30 p-3 space-y-1.5" data-sms-cost>
          <p className="text-xs">
            {cost.characters} <T>znakov</T> · {cost.segments} <T>segmentov</T> ×{' '}
            {formatCount(cost.recipients)} <T>príjemcov</T> ={' '}
            <strong data-sms-messages>{formatCount(cost.messages)}</strong> <T>SMS na faktúre</T>
          </p>
          {willBeTransliterated(body) && (
            <p className="text-xs text-amber-500" data-sms-transliterated>
              <T>Diakritika sa pred odoslaním odstráni. Zákazník dostane:</T>{' '}
              <span className="font-mono">{cost.sent}</span>
            </p>
          )}
        </div>
      )}

      {preview && body !== '' && (
        <div className="rounded-xl border border-border p-3 space-y-1" data-preview>
          <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
            <T>Ako to uvidí</T> {displayName(preview)}
          </p>
          {needsSubject && subject !== '' && (
            <p className="text-xs font-semibold">{subject}</p>
          )}
          <p className="text-xs whitespace-pre-wrap">
            {audience.channel === 'sms'
              ? smsCost(renderForCustomer(body, preview), 1).sent
              : renderForCustomer(body, preview)}
          </p>
        </div>
      )}

      {/* §9.336 - for one recipient a count says nothing, so the dialog names them. */}
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false)
          send()
        }}
        title={
          onlyRecipient === ''
            ? `${tt('Odoslať')} ${recipients} ${tt('príjemcom?')}`
            : `${tt('Odoslať')} - ${onlyRecipient}`
        }
        description={
          audience.channel === 'sms'
            ? `${tt('Zaplatí sa')} ${formatCount(cost.messages)} ${tt('SMS. Odoslané sa nedá vrátiť.')}`
            : tt('Odoslané sa nedá vrátiť.')
        }
        confirmLabel={tt('Odoslať')}
        loading={pending}
      />
    </Modal>
  )
}

// ─── points ──────────────────────────────────────────────────────────────────

function PointsDialog({
  customers,
  selected,
  onClose,
  onDone,
}: {
  customers: Customer[]
  selected: ReadonlySet<number>
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [raw, setRaw] = useState('')
  // §9.774, owner O135 - ticked by default: the owner expected the customers to be told.
  const [tell, setTell] = useState(true)
  const delta = pointsDelta(raw)
  const problems = pointsProblems(raw, selected.size)
  const enrols = enrolCount(customers, selected, delta)
  const clamped = clampedCount(customers, selected, delta)

  const grant = usePostCustomersBulkPoints({
    mutation: {
      onSuccess: (result: unknown) => {
        const answer = result as { updated?: number; notifying?: number }
        const note = pointsNoticeNote(tell, answer.notifying)
        toast({
          title: `${answer.updated ?? 0} ${tt('zákazníkov upravených')}`,
          ...(note === '' ? {} : { description: note }),
        })
        onDone()
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Body sa nepodarilo upraviť.'),
          variant: 'destructive',
        }),
    },
  })

  return (
    <Modal
      title={`${tt('Body')} - ${selected.size} ${tt('zákazníkov')}`}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={() =>
              grant.mutate({ data: { customerIds: [...selected], points: delta, tellCustomers: tell } })
            }
            disabled={problems.length > 0 || grant.isPending}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {grant.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Upraviť body</T>}
          </button>
        </>
      }
    >
      <Field label={tt('Body (kladné pripíše, záporné odpíše)')}>
        <input
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          aria-label={tt('Body')}
          placeholder="50"
          className={INPUT}
        />
      </Field>

      <TellCustomers checked={tell} onChange={setTell} many />

      {/* Both of these are in `adjustPoints`, and legacy describes neither in numbers. */}
      {enrols > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-amber-500" data-points-enrol>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{enrols}</strong>{' '}
            <T>z nich ešte nie je vo vernostnom programe - pripísaním bodov ich doň zaradíte.</T>
          </span>
        </p>
      )}
      {clamped > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-points-clamped>
          <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
          <span>
            <strong>{clamped}</strong>{' '}
            <T>z nich má menej bodov, než chcete odpísať - server ich zarovná na nulu.</T>
          </span>
        </p>
      )}
      {problems.includes('not-whole') && (
        <p className="text-xs text-red-500" data-points-problem>
          <T>Body sú celé čísla.</T>
        </p>
      )}
      {problems.includes('zero') && (
        <p className="text-xs text-red-500" data-points-problem>
          <T>Nula nič nezmení.</T>
        </p>
      )}
    </Modal>
  )
}

/**
 * *Upozorniť* - FRONTEND.md §9.774, BACKEND.md §10A.428, owner O135. Says what goes out, so a ticked box over an
 * event with nothing switched on is not a promise: push either way, the loyalty program's e-mail or SMS for a grant
 * where those are on, all as *Pripísané body* is set in *Notifikácie*.
 */
function TellCustomers({
  checked,
  onChange,
  many,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  many: boolean
}) {
  return (
    <label className="flex items-start gap-2 text-xs text-muted-foreground" data-points-tell>
      {/* A short name of its own: the sentence beside it says *Pripísané body*, and a label containing *body*
          made the points field's own *Body* ambiguous to anything finding a control by its name. */}
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={many ? tt('Upozorniť zákazníkov') : tt('Upozorniť zákazníka')}
        className="mt-0.5"
      />
      <span>
        <span className="font-semibold text-foreground">
          {many ? <T>Upozorniť zákazníkov</T> : <T>Upozorniť zákazníka</T>}
        </span>{' '}
        -{' '}
        <T>
          push pri pripísaní aj odpísaní, pri pripísaní aj e-mail alebo SMS z Vernostného programu, ak sú zapnuté -
          podľa Nastavenia - Notifikácie, Pripísané body.
        </T>
      </span>
    </label>
  )
}

// ─── anonymisation ───────────────────────────────────────────────────────────

function AnonymiseDialog({
  ids,
  onClose,
  onDone,
}: {
  ids: number[]
  onClose: () => void
  onDone: () => void
}) {
  const { toast } = useToast()
  const [typed, setTyped] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const phrase = anonymisePhrase(ids.length)
  const confirmed = anonymiseConfirmed(typed, ids.length)

  const start = usePostCustomersBulkAnonymize({
    mutation: {
      onSuccess: (result: unknown) => {
        const id = (result as { jobId?: string | null }).jobId ?? null
        if (id === null) {
          toast({ title: tt('Nič na anonymizáciu') })
          onDone()
          return
        }
        setJobId(id)
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Anonymizácia sa nespustila.'),
          variant: 'destructive',
        }),
    },
  })

  // The job runs on the server and survives this dialog closing; the poll only watches it.
  const { data: progressData } = useGetCustomersBulkAnonymizeJobId(jobId ?? '', {
    query: { enabled: jobId !== null, refetchInterval: 1500 },
  })
  const progress = (progressData ?? null) as AnonymiseProgress | null
  const finished = isFinished(progress)

  useEffect(() => {
    if (!finished) return
    if (progress?.status === 'error') {
      toast({
        title: tt('Anonymizácia zlyhala'),
        description: progress.errorMessage ?? tt('Neznáma chyba.'),
        variant: 'destructive',
      })
    } else {
      toast({
        title: tt('Anonymizované'),
        description: `${progress?.anonymized ?? 0}/${progress?.total ?? 0}`,
      })
    }
    onDone()
  }, [finished, progress, toast, onDone])

  return (
    <Modal
      title={`${tt('Anonymizovať')} ${ids.length} ${tt('zákazníkov?')}`}
      onClose={onClose}
      footer={
        jobId === null ? (
          <>
            <button
              type="button"
              onClick={onClose}
              className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            >
              <T>Zrušiť</T>
            </button>
            <button
              type="button"
              onClick={() =>
                start.mutate({ data: { customerIds: ids, anonymizeOrders: true } as never })
              }
              disabled={!confirmed || start.isPending}
              className={cn('flex-1 px-4 py-2.5 rounded-xl text-sm', BUTTON_DANGER)}
            >
              {start.isPending ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Anonymizovať</T>}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zatvoriť</T>
          </button>
        )
      }
    >
      {jobId === null ? (
        <>
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 space-y-1 text-xs text-red-500">
            <p className="font-bold">
              <T>GDPR - právo na zabudnutie. Nezvratné.</T>
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>
                <T>meno sa zmení na RemovedN</T>
              </li>
              <li>
                <T>e-mail, telefón, adresy, heslo a referral kód sa vymažú</T>
              </li>
              <li>
                <T>body sa vynulujú, marketing sa vypne, účet sa deaktivuje</T>
              </li>
              <li>
                <T>v objednávkach sa vymažú osobné údaje; sumy a položky zostanú pre účtovníctvo</T>
              </li>
            </ul>
          </div>
          <Field label={`${tt('Napíšte')} ${phrase}`}>
            <input
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              aria-label={tt('Potvrdzovacia fráza')}
              placeholder={phrase}
              className={cn(INPUT, 'font-mono')}
            />
          </Field>
          <p className="text-[11px] text-muted-foreground">
            {/* Legacy compares with trim() alone, so a lowercase answer is refused silently. */}
            <T>Veľké a malé písmená nerozhodujú.</T>
          </p>
        </>
      ) : (
        <div className="space-y-2" data-anonymise-progress>
          <div className="flex justify-between text-xs">
            <span>
              <T>Spracované</T> {progress?.processed ?? 0} / {progress?.total ?? ids.length}
            </span>
            <span>{progressPercent(progress)} %</span>
          </div>
          <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${progressPercent(progress)}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            <T>Beží na serveri - okno sa dá zatvoriť, práca pokračuje.</T>
          </p>
        </div>
      )}
    </Modal>
  )
}

// ─── the drawer ──────────────────────────────────────────────────────────────

function CustomerDrawer({
  customerId,
  levels,
  onClose,
  onChanged,
}: {
  customerId: number
  levels: LoyaltyLevel[]
  onClose: () => void
  onChanged: () => void
}) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { data, isLoading } = useGetCustomersCustomerId(customerId)
  const customer = (data ?? {}) as CustomerDetail
  const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent')
  const [discountValue, setDiscountValue] = useState('')

  /* §9.758, BACKEND.md §10A.413 - the delete asks `marketing_customers_delete` and the e-mail button
     `marketing_customers_email`, as the server does now. It asked `systemRole === 'main_admin'` (§9.259), which was
     the server's guard then. */
  const { hasPermission } = useAuth()
  const may = customerActionsFor(hasPermission)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const remove = useDeleteCustomersCustomerId()
  const patch = usePatchCustomersCustomerId({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries()
        onChanged()
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Zmena sa neuložila.'),
          variant: 'destructive',
        }),
    },
  })

  const change = (body: Record<string, unknown>) =>
    patch.mutate({ customerId, data: body })

  const average = averageOrderCents(customer)
  const discountIssues = discountProblems(discountType, discountValue, average)

  // ─── §9.336, the owner's 5b ────────────────────────────────────────────────
  // Two sections legacy's drawer has and this one did not, both routed through the functions and
  // hooks the bulk bar one level up already uses. Nothing here re-decides anything.
  const [sending, setSending] = useState<Channel | null>(null)
  const [pointsRaw, setPointsRaw] = useState('')
  // §9.774, owner O135 - ticked by default, as in the bulk dialog.
  const [tellCustomer, setTellCustomer] = useState(true)
  const oneSelected = useMemo(() => new Set([customerId]), [customerId])
  const typedPoints = pointsDelta(pointsRaw)
  const pointsIssues = pointsProblems(pointsRaw, 1)
  const pointsBlocked =
    pointsIssues.includes('not-a-number') ||
    pointsIssues.includes('zero') ||
    pointsIssues.includes('not-whole')

  const grantPoints = usePostCustomersBulkPoints({
    mutation: {
      onSuccess: (result: unknown) => {
        setPointsRaw('')
        const note = pointsNoticeNote(tellCustomer, (result as { notifying?: number }).notifying)
        if (note !== '') toast({ title: tt('Body upravené'), description: note })
        void queryClient.invalidateQueries()
        onChanged()
      },
      onError: (error: unknown) =>
        toast({
          title: tt('Chyba'),
          description: messageOf(error) ?? tt('Body sa nepodarilo upraviť.'),
          variant: 'destructive',
        }),
    },
  })

  const adjustPoints = (signed: number) => {
    if (signed === 0 || grantPoints.isPending) return
    grantPoints.mutate({ data: { customerIds: [customerId], points: signed, tellCustomers: tellCustomer } })
  }

  /* §9.495, `TODO.md` 3f - **a drawer that declares it is modal, so it keeps the promise.** 3f excluded
     `OrderSidePanel` as *a panel, not a modal*, correctly: that one declares neither attribute and promises
     nothing. This one slides in from the right and says `role="dialog"` plus `aria-modal="true"`, so by the
     census's own rule it either confines the keyboard or comes off the list. */
  const drawerRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: drawerKeyDown } = useModalFocus(true, drawerRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={displayName(customer)}
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-card border-l border-border h-full overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
        ref={drawerRef}
        onKeyDown={drawerKeyDown}
        data-customer-drawer
      >
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-bold truncate">{displayName(customer)}</h2>
            <p className="text-xs text-muted-foreground truncate">
              {customer.email || customer.phone || `ID ${customerId}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <Stat label={tt('Objednávky')} value={String(customer.orderCount ?? 0)} />
              <Stat
                label={tt('Tržba')}
                value={formatCents(average * (customer.orderCount ?? 0))}
              />
              <Stat label={tt('Body')} value={String(customer.pointsBalance ?? 0)} />
              <Stat label="Loyalty" node={<LevelBadge customer={customer} levels={levels} />} />
            </div>

            {/* §9.336, the owner's 5b: *"pošli nitifkáciu - email, sms, push"*. The same three
              * channels the bulk bar composes on, given a selection of one - so the eligibility
              * verdict, the variable check, the SMS cost and the confirmation are the proved ones.
              * A button says on its face why it cannot be used, which is what the audience box
              * behind it says in a sentence. */}
            <Section title={tt('Pošli notifikáciu')}>
              <div className="grid grid-cols-3 gap-2">
                {(['email', 'sms', 'push'] as Channel[]).map((channel) => {
                  const reason = ineligibleReason(customer, channel)
                  // §9.758 - the e-mail asks its right; the other two channels are the server's `{main_admin, admin}`.
                  const refused = channel === 'email' && !may.email
                  return (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => setSending(channel)}
                      disabled={reason !== null || refused}
                      data-send-channel={channel}
                      data-send-refused={refused ? 'right' : undefined}
                      title={refused ? emailRefusal() : reason === null ? undefined : exclusionText(reason, 1)}
                      className="flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border border-border text-xs font-semibold hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {channel === 'email' ? (
                        <Mail className="w-4 h-4" />
                      ) : channel === 'sms' ? (
                        <MessageSquare className="w-4 h-4" />
                      ) : (
                        <Bell className="w-4 h-4" />
                      )}
                      {channel === 'email' ? tt('E-mail') : channel === 'sms' ? 'SMS' : 'Push'}
                    </button>
                  )
                })}
              </div>
              <p className="text-[11px] text-muted-foreground">
                <T>Odchádza jednému človeku. Dialóg pred odoslaním povie komu a na akú adresu.</T>
              </p>
            </Section>

            {/* §9.336 - legacy's shape: an absolute value and the sign on the button. A typed minus
              * left over from the previous edit is how points come off a customer who was meant to
              * be rewarded, and this drawer has one field rather than a dialog per action. */}
            <Section title={tt('Body')}>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold tabular-nums text-amber-500" data-points-balance>
                  {customer.pointsBalance ?? 0}
                </span>
                <span className="text-xs text-muted-foreground">
                  <T>aktuálny zostatok</T>
                </span>
              </div>
              <div className="flex gap-2">
                <input
                  value={pointsRaw}
                  onChange={(event) => setPointsRaw(event.target.value)}
                  inputMode="numeric"
                  aria-label={tt('Počet bodov')}
                  placeholder={tt('napr. 50')}
                  className={cn(INPUT, 'flex-1')}
                />
                <button
                  type="button"
                  onClick={() => adjustPoints(Math.abs(typedPoints))}
                  disabled={pointsBlocked}
                  data-points-add
                  className="px-3 py-2 rounded-lg border border-emerald-500/40 text-emerald-500 text-xs font-bold hover:bg-emerald-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  + <T>Pripísať</T>
                </button>
                <button
                  type="button"
                  onClick={() => adjustPoints(-Math.abs(typedPoints))}
                  disabled={pointsBlocked}
                  data-points-subtract
                  className="px-3 py-2 rounded-lg border border-red-500/40 text-red-500 text-xs font-bold hover:bg-red-500/10 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  - <T>Odpísať</T>
                </button>
              </div>
              <TellCustomers checked={tellCustomer} onChange={setTellCustomer} many={false} />
              {/* Both of these are things the endpoint does besides moving the number, and the same
                * two functions state them in the bulk dialog. */}
              {enrolCount([customer], oneSelected, Math.abs(typedPoints)) > 0 && (
                <p className="text-xs text-amber-500" data-points-enrol>
                  <T>Pripísanie ho zaradí do vernostného programu.</T>
                </p>
              )}
              {clampedCount([customer], oneSelected, -Math.abs(typedPoints)) > 0 && (
                <p className="text-xs text-muted-foreground" data-points-clamped>
                  <T>Odpísanie by šlo pod nulu - server zarovná zostatok na 0.</T>
                </p>
              )}
              {pointsIssues.includes('not-whole') && (
                <p className="text-xs text-red-500" data-points-problem>
                  <T>Body sú celé čísla.</T>
                </p>
              )}
            </Section>

            {/* Consent, per channel, with the reason it cannot be used. */}
            <Section title={tt('Marketing')}>
              <ConsentRow
                label={tt('E-mail marketing')}
                detail={customer.email ?? tt('bez e-mailu')}
                checked={customer.marketingEmailOptIn !== false}
                disabled={(customer.email ?? '') === '' || patch.isPending}
                onChange={(next) => change({ marketingEmailOptIn: next })}
                testId="consent-email"
              />
              <ConsentRow
                label={tt('SMS marketing')}
                detail={customer.phone ?? tt('bez telefónu')}
                checked={customer.marketingSmsOptIn !== false}
                disabled={(customer.phone ?? '') === '' || patch.isPending}
                onChange={(next) => change({ marketingSmsOptIn: next })}
                testId="consent-sms"
              />
              {/* §9.735, owner O104: *"keď si otvorím zákazníka nevidím ich tam že zapnúť vypnúť"*. A switch beside
                * the two above where there was a count: on when the customer has a device, off removes every one
                * after a question (§10A.381), and on is the customer's own, in their browser. */}
              <PushConsentRow
                customerId={customerId}
                devices={customer.pushSubscriptionCount}
                onRemoved={() => {
                  void queryClient.invalidateQueries()
                  onChanged()
                }}
              />
              <p className="text-xs text-muted-foreground">
                <T>Newsletter:</T>{' '}
                {customer.newsletterSubscribed === true ? <T>prihlásený</T> : <T>neprihlásený</T>}
              </p>
            </Section>

            {/* A discount applied to every future order - so it is checked before it is set. */}
            <Section title={tt('Trvalá osobná zľava')}>
              {hasPersonalDiscount(customer) ? (
                <div className="flex items-center justify-between gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-bold">
                      <T>Aktívna</T>
                    </p>
                    <p className="text-lg font-bold text-emerald-500" data-discount-active>
                      {discountLabel(customer.personalDiscountType ?? null, customer.personalDiscountValue)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => change({ personalDiscountType: null, personalDiscountValue: 0 })}
                    disabled={patch.isPending}
                    className="px-3 py-1.5 rounded-lg border border-red-500/40 text-red-500 text-xs font-bold hover:bg-red-500/10 disabled:opacity-50"
                  >
                    <T>Odstrániť</T>
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setDiscountType('percent')}
                      className={cn(
                        'py-2 rounded-lg border-2 text-xs font-bold',
                        discountType === 'percent' ? 'border-primary bg-primary/10 text-primary' : 'border-border',
                      )}
                    >
                      % <T>Percento</T>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscountType('fixed')}
                      className={cn(
                        'py-2 rounded-lg border-2 text-xs font-bold',
                        discountType === 'fixed' ? 'border-primary bg-primary/10 text-primary' : 'border-border',
                      )}
                    >
                      € <T>Pevná suma</T>
                    </button>
                  </div>
                  <input
                    value={discountValue}
                    onChange={(event) => setDiscountValue(event.target.value)}
                    aria-label={tt('Hodnota zľavy')}
                    className={INPUT}
                  />
                  {discountIssues.includes('percent-over-100') && (
                    <p className="text-xs text-red-500" data-discount-problem>
                      <T>Viac ako 100 % sa zľaviť nedá.</T>
                    </p>
                  )}
                  {discountIssues.includes('negative') && (
                    <p className="text-xs text-red-500" data-discount-problem>
                      <T>Zľava nemôže byť záporná.</T>
                    </p>
                  )}
                  {discountIssues.includes('more-than-they-spend') && (
                    <p className="text-xs text-amber-500" data-discount-problem>
                      <T>To je viac, než tento zákazník priemerne utratí</T> ({formatCents(average)}
                      ). <T>Platilo by to pri každej ďalšej objednávke.</T>
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      change({
                        personalDiscountType: discountType,
                        personalDiscountValue: Number(discountValue.replace(',', '.')),
                      })
                    }
                    disabled={
                      patch.isPending ||
                      discountIssues.includes('no-value') ||
                      discountIssues.includes('percent-over-100') ||
                      discountIssues.includes('negative')
                    }
                    className={cn('w-full px-3 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
                  >
                    <T>Nastaviť zľavu</T>
                  </button>
                </div>
              )}
            </Section>

            <Section title={`${tt('Posledné objednávky')} (${(customer.orders ?? []).length})`}>
              {(customer.orders ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  <T>Žiadne objednávky.</T>
                </p>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1" data-customer-orders>
                  {(customer.orders ?? []).slice(0, 50).map((order, index) => (
                    <div
                      key={order.id ?? index}
                      className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-3 py-2 text-xs"
                    >
                      <div className="min-w-0">
                        {/* §9.143 - this list is a record of past orders, so each is a way back. */}
                        <p className="font-mono">
                          <OrderNumberLink
                            orderId={order.id}
                            className="text-primary hover:underline"
                            title={`${tt('Otvoriť v archíve')}: #${order.orderNumber ?? ''}`}
                          >
                            #{order.orderNumber}
                          </OrderNumberLink>
                        </p>
                        <p className="text-muted-foreground truncate">
                          {/* §9.259 - this read *delivered · card_courier*. Both mappings are in
                            * `shared` already and this line used neither. */}
                          {formatDay(order.createdAt)} · {orderStatusLabel(order.status)} ·{' '}
                          {paymentLabel(order.paymentMethod)}
                        </p>
                      </div>
                      <span className="font-mono whitespace-nowrap">
                        {formatCents(Math.round(Number(order.total ?? 0) * 100))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section title={tt('Účet')}>
              {/* §9.787, owner O140 - banning the customer, above the account switch it is not the same as. */}
              {may.block && <CustomerBlockSection customerId={customerId} />}
              <button
                type="button"
                onClick={() =>
                  change({
                    deletionRequestedAt: customer.deletionRequestedAt ? null : new Date().toISOString(),
                  })
                }
                disabled={patch.isPending}
                className={cn(
                  'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold disabled:opacity-50',
                  customer.deletionRequestedAt
                    ? 'border-border hover:bg-muted/50'
                    : 'border-red-500/40 text-red-500 hover:bg-red-500/10',
                )}
                data-account-toggle
              >
                {customer.deletionRequestedAt ? (
                  <>
                    <UserCheck className="w-4 h-4" />
                    <T>Reaktivovať účet</T>
                  </>
                ) : (
                  <>
                    <UserX className="w-4 h-4" />
                    <T>Deaktivovať účet</T>
                  </>
                )}
              </button>
              <p className="text-[11px] text-muted-foreground">
                <T>
                  Deaktivovaný zákazník sa nemôže prihlásiť a táto obrazovka mu nič neposiela.
                  Anonymizácia je samostatná, nezvratná akcia.
                </T>
              </p>

              {/* §9.259 - the owner's O4: a waiter mistyped an e-mail at the till and the record
                * could not be taken away. `DELETE /customers/{id}` asks `marketing_customers_delete` since
                * §9.758, so the button does too - offering one the server would refuse is how a screen
                * teaches somebody it is broken. */}
              {may.delete && (
                <>
                  <button
                    type="button"
                    data-customer-delete={customerId}
                    onClick={() => setConfirmDelete(true)}
                    disabled={remove.isPending}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-red-500/40 text-red-500 text-sm font-semibold hover:bg-red-500/10 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    <T>Zmazať zákazníka</T>
                  </button>
                  <p className="text-[11px] text-muted-foreground">
                    <T>
                      Pre záznam, ktorý vznikol omylom - napríklad preklepom v e-maile pri telefonickej
                      objednávke.
                    </T>
                  </p>
                </>
              )}
            </Section>
          </div>
        )}
      </div>

      {/* §9.336 - the bulk composer, on an audience of one. `audienceOfOne` is why this needs no
        * second dialog: the eligibility verdict, the excluded-and-why box, the unknown-variable
        * refusal and the SMS cost all come from the same functions the selection uses. */}
      {sending !== null && (
        <ComposeDialog
          audience={audienceOfOne(customer, sending)}
          onClose={() => setSending(null)}
          onSent={() => {
            setSending(null)
            onChanged()
          }}
        />
      )}

      {/* What goes and what stays are different things, and only one of them is obvious. `orders`
        * carries no `customer_id` — the name, phone and address on each order are snapshots — so the
        * books cannot be touched by this. */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => {
          void remove
            .mutateAsync({ customerId })
            .then(() => {
              setConfirmDelete(false)
              onChanged()
              onClose()
            })
            // §9.395 - **this closed the dialog and said nothing**, and the mutation carries no
            // `onError` either, so a refused delete was indistinguishable from a done one: the
            // operator watched the dialog close and believed the customer was gone.
            .catch((caught: unknown) => {
              setConfirmDelete(false)
              toast({
                variant: 'destructive',
                title: tt('Zákazník nebol zmazaný'),
                description: apiErrorMessage(caught, tt('Server zmazanie odmietol.')),
              })
            })
        }}
        loading={remove.isPending}
        variant="danger"
        title={tt('Zmazať zákazníka?')}
        description={tt(
          'Zmažú sa body, uložené adresy a vernostné zaradenie. Objednávky zostávajú - každá si nesie meno a adresu, s ktorými bola zadaná.',
        )}
        confirmLabel={tt('Zmazať')}
        cancelLabel={tt('Zrušiť')}
      />
    </div>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

function ConsentMark({ channel, customer }: { channel: Channel; customer: Customer }) {
  const reason = ineligibleReason(customer, channel)
  const letter = channel === 'email' ? 'E' : 'S'
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-5 h-5 rounded font-bold',
        reason === null ? 'bg-emerald-500/15 text-emerald-500' : 'bg-muted text-muted-foreground',
      )}
      title={
        reason === null
          ? channel === 'email'
            ? tt('Súhlasí s e-mailmi')
            : tt('Súhlasí so SMS')
          : exclusionText(reason, 1).replace(' (1)', '')
      }
      data-consent-mark={`${channel}-${reason ?? 'ok'}`}
    >
      {letter}
    </span>
  )
}

function LevelBadge({ customer, levels }: { customer: Customer; levels: LoyaltyLevel[] }) {
  const key = levelKeyOf(customer)
  if (customer.isLoyalty !== true || key === '') {
    return <span className="text-xs text-muted-foreground">-</span>
  }
  const level = levels.find((candidate) => candidate.key === key)
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-amber-500/15 text-amber-500">
      <span>{level?.emoji ?? '⭐'}</span>
      {level?.name ?? key}
    </span>
  )
}

function BulkButton({
  icon: Icon,
  label,
  count,
  tone,
  onClick,
}: {
  icon: typeof Mail
  label: string
  count?: number
  tone?: 'red'
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold',
        tone === 'red'
          ? 'border-red-500/40 text-red-500 hover:bg-red-500/10'
          : 'border-border hover:bg-muted/50',
      )}
    >
      <Icon className="w-3 h-3" />
      {label}
      {count !== undefined && ` (${count})`}
    </button>
  )
}

function Stat({ label, value, node }: { label: string; value?: string; node?: ReactNode }) {
  return (
    <div className="rounded-xl bg-muted/40 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-bold text-sm">{node ?? value}</p>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  )
}

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}

function Modal({
  title,
  onClose,
  footer,
  wide,
  children,
}: {
  title: string
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
  children: ReactNode
}) {
  /* §9.495 - **the fifth copy of this component**, and the fifth time this four-line change has been made to
     it (§9.494 counted them). The extraction is specified in `TODO.md` and still deferred: a refactor across
     five screens with no e2e available. The close is unconditional because a generic `Modal` takes no busy flag
     and its wrapper already closes on a backdrop click with no guard (§9.491). */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          'bg-card border border-border rounded-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl',
          wide ? 'max-w-2xl' : 'max-w-lg',
        )}
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-compose-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm truncate">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted shrink-0"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">{children}</div>
        {footer && <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">{footer}</div>}
      </div>
    </div>
  )
}

/**
 * A stamp as a date, or a dash.
 *
 * **§9.3.** This called `toLocaleDateString('sk-SK')` with no options, which renders `27. 7. 2026` where
 * every other page in the app renders `27. 07. 2026` — and with no zone, so near midnight it could name a
 * different day than the rest of the screen. Through the shared formatter now; the `—` is this page's own.
 */
function formatDay(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return sharedFormatDay(stamp)
}

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}
